import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import styled from 'styled-components';

import * as colors from '../../shared/colors';
import { format, isSameDay } from '../../shared/dateFns';
import { Dispatch, State, calendarSelectDay } from '../state';

interface IProps {
    day: Date;
}

const Wrapper = styled.div`
    cursor: pointer;
    padding-top: 10px;
    padding-bottom: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    font-size: 1.1rem;
    width: 2rem;
`;

const Selected = styled.div`
    position: absolute;
    background-color: ${colors.primaryColor};
    border-radius: 50%;
    top: calc(50% - 1rem);
    left: calc(50% - 1rem);
    width: 2rem;
    height: 2rem;
`;

const HasEvent = styled.div`
    position: absolute;
    bottom: 3px;
    left: calc(50% - 0.3rem);
    background-color: ${colors.deadline};
    border-radius: 50%;
    width: 0.6rem;
    height: 0.6rem;
`;

interface IDateNumber {
    $selected: boolean;
}

const DateNumber = styled.div<IDateNumber>`
    z-index: 1;
    color: ${({ $selected }) => $selected ? colors.white : colors.text};
`;

const hasEventSelector = createSelector(
    (state: State, _: Date) => state.calendar.events.ids,
    (state: State, _: Date) => state.calendar.events.dict,
    (_: State, day: Date) => day,
    (ids, dict, day): boolean => (
        ids.map(id => dict[id]).filter(event => isSameDay(event.start, day)).length > 0
    ),
);

function Day({ day }: IProps): JSX.Element {
    const dispatch = useDispatch<Dispatch>();
    const selectedDate = useSelector((state: State) => state.calendar.selectedDate);
    const selected = selectedDate ? isSameDay(selectedDate, day) : false;
    const hasEvent = useSelector((state: State) => hasEventSelector(state, day));
    const select = () => {
        dispatch(calendarSelectDay(day));
    };

    return (<Wrapper onClick={select}>
        {selected && <Selected />}
        <DateNumber $selected={selected}>{format(day, 'd')}</DateNumber>
        {hasEvent && <HasEvent />}
    </Wrapper>);
}

export default React.memo(Day);
